import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Copy, Download, FileText, Mail, Phone } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO, { breadcrumbJsonLd } from '../components/SEO';
import { BOILERPLATE, FAST_FACTS, LOGOS, PRESS_CONTACT, PRESS_DOCS, PRODUCT_PHOTOS } from '../data/pressKit';
import { formatNewsDate, newsPosts } from '../data/news';

const DownloadLink = ({ href, label, testid }) => (
  <a
    href={href}
    download
    data-testid={testid}
    className="inline-flex items-center gap-2 text-sm font-semibold text-blue-600 hover:text-blue-800"
  >
    <Download className="w-4 h-4" /> {label}
  </a>
);

const CopyBlock = ({ id, title, text }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard unavailable */ }
  };
  return (
    <div className="bg-slate-50 rounded-2xl border border-slate-200 p-6" data-testid={`boilerplate-${id}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold text-[#1e2d3b]">{title}</h3>
        <button
          type="button"
          onClick={copy}
          data-testid={`boilerplate-${id}-copy`}
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-slate-300 text-slate-600 hover:bg-[#1e2d3b] hover:text-white hover:border-[#1e2d3b] transition-colors"
        >
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <p className="text-slate-700 leading-relaxed text-sm sm:text-base">{text}</p>
    </div>
  );
};

const Section = ({ id, title, intro, children }) => (
  <section id={id} className="py-14 border-t border-slate-100">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <h2 className="text-2xl sm:text-3xl font-bold text-[#1e2d3b] mb-2">{title}</h2>
      {intro && <p className="text-slate-600 mb-8 max-w-3xl">{intro}</p>}
      {children}
    </div>
  </section>
);

const PressKitPage = ({ onRequestDemo, onRequestQuote }) => (
  <div className="min-h-screen bg-white">
    <SEO
      title="Press Kit & Media Resources — ctrl.aero"
      description="Official CTRL Systems logos, product photography, company boilerplate, fast facts, and media contact for journalists and partners."
      canonical="https://ctrl.aero/press"
      jsonLd={breadcrumbJsonLd([
        { name: 'Home', url: 'https://ctrl.aero/' },
        { name: 'Press Kit', url: 'https://ctrl.aero/press' },
      ])}
    />
    <Header onRequestDemo={onRequestDemo} onRequestQuote={onRequestQuote} />

    <section className="pt-32 pb-16 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs font-semibold tracking-widest uppercase text-orange-400 mb-4">Media Resources</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">Press Kit</h1>
        <p className="text-lg text-slate-300 max-w-3xl">
          Approved logos, product photography, boilerplate, and fast facts about CTRL Systems — everything journalists,
          partners, and distributors need to cover CTRL accurately.
        </p>
        <a
          href="/docs/ctrl-systems-press-kit.zip"
          download
          data-testid="press-kit-zip-download"
          className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-semibold transition-colors"
        >
          <Download className="w-5 h-5" /> Download Full Press Kit (ZIP, 16 MB)
        </a>
        <nav className="mt-6 flex flex-wrap gap-3 text-sm" aria-label="Press kit sections">
          {[['logos', 'Logos'], ['photos', 'Product Photos'], ['boilerplate', 'Boilerplate'], ['facts', 'Fast Facts'], ['releases', 'Press Releases'], ['contact', 'Media Contact']].map(([id, label]) => (
            <a key={id} href={`#${id}`} className="px-4 py-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors" data-testid={`press-nav-${id}`}>
              {label}
            </a>
          ))}
        </nav>
      </div>
    </section>

    <Section id="logos" title="Logos" intro="Use the CTRL Systems logo unaltered. Do not stretch, recolor, or add effects. Maintain clear space equal to the height of the 'C' on all sides.">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="press-logos">
        {LOGOS.map((l) => (
          <div key={l.id} className="rounded-2xl border border-slate-200 overflow-hidden" data-testid={`press-logo-${l.id}`}>
            <div className={`${l.bg} h-40 flex items-center justify-center p-8`}>
              <img src={l.file} alt={l.name} className="max-h-full max-w-full object-contain" loading="lazy" />
            </div>
            <div className="p-5">
              <h3 className="font-semibold text-[#1e2d3b] text-sm">{l.name}</h3>
              <p className="text-xs text-slate-500 mt-1 mb-3">{l.format} · {l.note}</p>
              <DownloadLink href={l.file} label="Download PNG" testid={`press-logo-${l.id}-download`} />
            </div>
          </div>
        ))}
      </div>
    </Section>

    <Section id="photos" title="Product Photography" intro="High-resolution images for editorial use. Credit: CTRL Systems, Inc.">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="press-photos">
        {PRODUCT_PHOTOS.map((p) => (
          <figure key={p.id} className="rounded-2xl border border-slate-200 overflow-hidden flex flex-col" data-testid={`press-photo-${p.id}`}>
            <div className="h-56 bg-slate-50 flex items-center justify-center overflow-hidden">
              <img src={p.file} alt={p.name} className="w-full h-full object-contain" loading="lazy" />
            </div>
            <figcaption className="p-5 flex-1 flex flex-col">
              <h3 className="font-semibold text-[#1e2d3b] text-sm">{p.name}</h3>
              <p className="text-xs text-slate-500 mt-1 mb-3 flex-1">{p.caption}</p>
              <DownloadLink href={p.file} label="Download image" testid={`press-photo-${p.id}-download`} />
            </figcaption>
          </figure>
        ))}
      </div>
      <div className="mt-8 flex flex-wrap gap-6">
        {PRESS_DOCS.map((d) => (
          <a key={d.href} href={d.href} download className="inline-flex items-center gap-2 text-sm font-semibold text-[#1e2d3b] hover:text-blue-700" data-testid={`press-doc-${d.href.split('/').pop()}`}>
            <FileText className="w-4 h-4 text-blue-600" /> {d.name} (PDF)
          </a>
        ))}
      </div>
    </Section>

    <Section id="boilerplate" title="Company Boilerplate" intro="Approved descriptions of CTRL Systems for use in articles and press materials.">
      <div className="grid lg:grid-cols-2 gap-6">
        <CopyBlock id="short" title="Short (one paragraph)" text={BOILERPLATE.short} />
        <CopyBlock id="long" title="Extended" text={BOILERPLATE.long} />
      </div>
    </Section>

    <Section id="facts" title="Fast Facts">
      <dl className="grid sm:grid-cols-2 gap-x-10 gap-y-5 max-w-4xl" data-testid="press-fast-facts">
        {FAST_FACTS.map((f) => (
          <div key={f.label} className="border-l-2 border-orange-400 pl-4">
            <dt className="text-xs font-semibold uppercase tracking-wider text-slate-500">{f.label}</dt>
            <dd className="text-[#1e2d3b] font-medium mt-1">{f.value}</dd>
          </div>
        ))}
      </dl>
    </Section>

    <Section id="releases" title="Recent Press Releases">
      <ul className="divide-y divide-slate-100 max-w-4xl" data-testid="press-releases">
        {newsPosts.map((p) => (
          <li key={p.slug} className="py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <Link to={`/news/${p.slug}`} className="font-medium text-[#1e2d3b] hover:text-blue-700" data-testid={`press-release-${p.slug}`}>
              {p.title}
            </Link>
            <div className="flex items-center gap-4 shrink-0 text-sm text-slate-500">
              <span>{formatNewsDate(p.date)}</span>
              {p.pdf && <DownloadLink href={p.pdf} label="PDF" testid={`press-release-${p.slug}-pdf`} />}
            </div>
          </li>
        ))}
      </ul>
    </Section>

    <Section id="contact" title="Media Contact">
      <div className="bg-[#1e2d3b] text-white rounded-2xl p-8 max-w-2xl" data-testid="press-media-contact">
        <p className="text-xl font-bold">{PRESS_CONTACT.name}</p>
        <p className="text-slate-300 text-sm mt-1">{PRESS_CONTACT.title}</p>
        <p className="text-slate-300 text-sm">{PRESS_CONTACT.company}</p>
        <div className="mt-5 flex flex-col sm:flex-row gap-4 text-sm">
          <a href={`mailto:${PRESS_CONTACT.email}`} className="inline-flex items-center gap-2 text-orange-300 hover:text-orange-200" data-testid="press-contact-email">
            <Mail className="w-4 h-4" /> {PRESS_CONTACT.email}
          </a>
          <a href={`tel:${PRESS_CONTACT.phone.replace(/[^+\d]/g, '')}`} className="inline-flex items-center gap-2 text-orange-300 hover:text-orange-200" data-testid="press-contact-phone">
            <Phone className="w-4 h-4" /> {PRESS_CONTACT.phone}
          </a>
        </div>
      </div>
    </Section>

    <Footer />
  </div>
);

export default PressKitPage;
